import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export const COMPLAINTS_PAGE_SIZE = 50;

/* Owns every piece of complaint-list state: primary filters, URL drill-down
   filters (chips), pagination and the fetch cycle. The page component stays
   purely presentational. */
export const useComplaintFilters = () => {
  const { user, fnFilter, setFnFilter } = useAuth();
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [outlets, setOutlets] = useState([]);

  const [filters, setFilters] = useState({
    search: params.get("search") || "",
    status: params.get("status") || "all",
    rag: params.get("rag") || "all",
    outlet_id: params.get("outlet_id") || "all",
    department: params.get("department") || "all",
    escalation_level: params.get("escalation_level") || "all",
    open_only: params.get("open") === "1",
  });
  // drill-down filters arriving via URL (shown as removable chips)
  const [extra, setExtra] = useState({
    category: params.get("category") || null,
    reopened: params.get("reopened") === "1" ? true : null,
    min_days: params.get("min_days"),
    max_days: params.get("max_days"),
    date_from: params.get("date_from"),
    date_to: params.get("date_to"),
    closed_from: params.get("closed_from"),
  });

  const effectiveFn = user?.function_scope !== "Both" ? user.function_scope : fnFilter;

  useEffect(() => {
    const fnParam = params.get("fn");
    if (fnParam && user?.function_scope === "Both" && ["Sales", "Service", "Both"].includes(fnParam)) {
      setFnFilter(fnParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get("/outlets").then((r) => setOutlets(r.data)).catch((e) => console.error("Background fetch failed:", e));
  }, []);

  const queryParams = useCallback(() => {
    const q = { page, page_size: COMPLAINTS_PAGE_SIZE, function: effectiveFn };
    if (filters.search) q.search = filters.search;
    if (filters.status !== "all") q.status = filters.status;
    if (filters.rag !== "all") q.rag = filters.rag;
    if (filters.outlet_id !== "all") q.outlet_id = filters.outlet_id;
    if (filters.department !== "all") q.department = filters.department;
    if (filters.escalation_level !== "all") {
      q.escalation_level = filters.escalation_level;
      q.sort = "escalation";
    }
    if (filters.open_only) q.open_only = true;
    if (extra.category) q.category = extra.category;
    if (extra.reopened) q.reopened = true;
    if (extra.min_days) q.min_days = extra.min_days;
    if (extra.max_days) q.max_days = extra.max_days;
    if (extra.date_from) q.date_from = extra.date_from;
    if (extra.date_to) q.date_to = extra.date_to;
    if (extra.closed_from) q.closed_from = extra.closed_from;
    return q;
  }, [filters, extra, page, effectiveFn]);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/complaints", { params: queryParams() })
      .then((r) => {
        setItems(r.data.items);
        setTotal(r.data.total);
      })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, [queryParams]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [filters, extra, effectiveFn]);

  const clearExtra = useCallback((key) => {
    setExtra((p) => {
      const n = { ...p, [key]: null };
      if (key === "min_days") n.max_days = null;
      if (key === "date_from") n.date_to = null;
      return n;
    });
  }, []);

  const pages = Math.max(1, Math.ceil(total / COMPLAINTS_PAGE_SIZE));

  return {
    user, items, total, page, setPage, pages, loading, outlets,
    filters, setFilters, extra, clearExtra, queryParams,
  };
};
